// The button in a Case's header that goes to the Case's consulting chat,
// making it (empty) when the Case has none, and the marks of a Case's chat and
// of a step's handed-off chat on their rows. The Cases part does not import
// this part: it shows the header actions and row marks registered for "case"
// and "step". On a row the state of the chat sits on the link's bubble.
//
// Promise:
// - Pressing it moves to the chat's page (the Case is one click away there,
//   in the chat's related area and side view). A failure is one line under
//   the button.
// - Inside that very chat (the Case in its side view) the button is absent.
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import type { AgentChatDTO } from "@trameria/core/agent-chat-shared";
import { m } from "@trameria/i18n";
import type { ReactNode } from "react";
import { useApi } from "../app/context";
import type {
  HeaderAction,
  HeaderActionData,
  HeaderActionProps,
  HeaderActionTarget,
  RowButtonProps,
  RowMark,
  RowNoteTarget,
} from "../app/screen-part";
import { apiErrorMessage } from "../data/errors";
import { ICON_BUTTON, SMALL_ICON_BOX } from "../ui/button";
import { Icon } from "../ui/icon";
import { Tooltip } from "../ui/tooltip";
import { agentOf, capabilitiesOf, modelOf } from "./conditions";
import { belowById, type ChatState, chatState } from "./list-rows";
import { ChatMark, STATE_LABEL } from "./mark";
import { agentName } from "./names";
import { chatListQuery, devicesQuery, useOpenCaseChat } from "./queries";

function CaseChatButton({ targetId, ext }: HeaderActionProps) {
  const navigate = useNavigate();
  const open = useOpenCaseChat();
  const { key: here } = useParams({ strict: false }) as { key?: string };
  const linked = ext?.agent as { key?: unknown; id?: unknown } | undefined;
  const key = typeof linked?.key === "string" ? linked.key : null;
  if (here && (here === key || here === linked?.id)) return null;
  const go = () => {
    if (key) {
      void navigate({ to: "/me/agents/$key", params: { key } });
      return;
    }
    open.mutate(targetId, {
      onSuccess: (chat) =>
        void navigate({ to: "/me/agents/$key", params: { key: chat.key } }),
    });
  };
  return (
    <div className="relative flex flex-none">
      {/* In the header on a phone too: the Dock's left slot is the way up
          (the person, 2026-10-18). */}
      <Tooltip text={m.agents_case_chat()}>
        <button
          type="button"
          aria-label={m.agents_case_chat()}
          disabled={open.isPending}
          onClick={go}
          className={ICON_BUTTON}
        >
          <Icon kind="agentChat" size={15} />
        </button>
      </Tooltip>
      {open.isError && (
        // Right under the button, floating so the header keeps its height.
        <p
          role="alert"
          className="absolute top-full right-0 z-10 mt-1 w-max max-w-[280px] rounded-[7px] border border-border bg-surface px-2.5 py-1 text-[12px] text-danger shadow-[var(--shadow-popover)]"
        >
          {apiErrorMessage(open.error, m.agents_case_chat_failed())}
        </p>
      )}
    </div>
  );
}

/** The same button as the outside's data: its words, icon and where it goes. */
function useCaseChatAction({
  targetId,
  ext,
}: HeaderActionTarget): HeaderActionData | null {
  const navigate = useNavigate();
  const open = useOpenCaseChat();
  const { key: here } = useParams({ strict: false }) as { key?: string };
  const linked = ext?.agent as { key?: unknown; id?: unknown } | undefined;
  const key = typeof linked?.key === "string" ? linked.key : null;
  if (here && (here === key || here === linked?.id)) return null;
  return {
    label: m.agents_case_chat(),
    icon: "agentChat",
    onPress: () => {
      if (key) {
        void navigate({ to: "/me/agents/$key", params: { key } });
        return;
      }
      open.mutate(targetId, {
        onSuccess: (chat) =>
          void navigate({ to: "/me/agents/$key", params: { key: chat.key } }),
      });
    },
  };
}

export const caseChatAction: HeaderAction = {
  id: "agent-chat",
  Component: CaseChatButton,
  useAction: useCaseChatAction,
};

/** The chat a Case or a step is linked to, from its `ext` (`agent`). */
const linkedChat = (
  ext: HeaderActionTarget["ext"],
): { id: string; key: string } | null => {
  const linked = ext?.agent as { key?: unknown; id?: unknown } | undefined;
  return typeof linked?.key === "string" && typeof linked.id === "string"
    ? { id: linked.id, key: linked.key }
    : null;
};

/**
 * What goes on in the linked chat, read from the open chats' list (one read
 * shared with the Agents screen); null when nothing does, or the chat is not
 * among the open ones. A step's chat is a child of its Case's chat, and is
 * told as the Agents list tells a child. `running`: its agent is at work.
 */
function useLinkedChatState(
  chat: { id: string } | null,
  child: boolean,
): {
  state: ChatState | null;
  running: boolean;
  run: AgentChatDTO["run"] | null;
  deviceId: string | null;
} {
  const list = useQuery({
    ...chatListQuery(useApi(), { workspace: null }),
    enabled: chat !== null,
  });
  const items = list.data?.items ?? [];
  const found = chat && items.find((c) => c.id === chat.id);
  if (!found) return { state: null, running: false, run: null, deviceId: null };
  return {
    state: chatState(found, belowById(items)(found.id), child),
    running: found.running !== null,
    run: found.run,
    deviceId: found.deviceId,
  };
}

/**
 * Straight to the linked chat's own page (the old web's; the row itself opens
 * the Case). Only when there is a chat. What goes on in the chat (a question,
 * working, unread) sits on the link's bubble at its top left, as on the
 * Agents list (the person 2026-10-20: one icon, not a mark after the name and
 * a bubble at the end); the link's name says it too.
 */
function ChatLink({
  ext,
  round,
  label,
  child,
}: RowButtonProps & { label: string; child: boolean }) {
  const chat = linkedChat(ext);
  const { state, running } = useLinkedChatState(chat, child);
  if (!chat) return null;
  const named = state ? `${label}: ${STATE_LABEL[state]()}` : label;
  return (
    <Tooltip text={named}>
      <Link
        to="/me/agents/$key"
        params={{ key: chat.key }}
        aria-label={named}
        // Muted, and on hover a shade darker than the row's own hover (the
        // old table's; the step's "…" beside the round one does the same).
        className={`${
          round
            ? "inline-flex h-[26px] w-[26px] flex-none items-center justify-center rounded-[13px]"
            : `${SMALL_ICON_BOX} -my-1`
        } cursor-pointer text-muted hover:bg-selected hover:text-body`}
      >
        <ChatMark
          badge={null}
          state={state}
          running={running}
          tone=""
          tip={false}
        />
      </Link>
    </Tooltip>
  );
}

/** At the end of a Case row's name: the way to the Case's chat. */
function CaseChatLink(props: RowButtonProps) {
  return <ChatLink {...props} label={m.agents_case_chat()} child={false} />;
}

/** At the end of a step row's name: the way to the chat it was handed to. */
function StepChatLink(props: RowButtonProps) {
  return (
    <ChatLink {...props} label={m.agents_delegate_step_open()} child={true} />
  );
}

export const caseChatRowMark: RowMark = {
  id: "agent-chat",
  Button: CaseChatLink,
};

/** The words for a handed-off step's chat at the head of its line (the old web's). */
const STEP_NOTE: Partial<Record<ChatState, () => string>> = {
  asking: () => m.agents_row_asking(),
  working: () => m.agents_row_working(),
};

/**
 * Under a handed-off step's title in its Case: what its chat does (a question,
 * working) in the accent color, then the model it runs with (its agent's name
 * when it has none, or the model is not among the device's). A closed step
 * says it only while its chat still moves.
 */
function useStepChatNote({ ext, closed }: RowNoteTarget): ReactNode {
  const chat = linkedChat(ext);
  const { state, run, deviceId } = useLinkedChatState(chat, true);
  // The devices' list is read once and shared with the Agents screens.
  const devices = useQuery({
    ...devicesQuery(useApi()),
    enabled: chat !== null && !closed,
  });
  if (!chat) return null;
  const words = state ? STEP_NOTE[state] : undefined;
  if (closed)
    return words ? <span className="text-accent">{words()}</span> : null;
  const device = devices.data?.find((d) => d.id === deviceId) ?? null;
  const caps = capabilitiesOf(device);
  const kind = run?.agentKind ?? null;
  const model = modelOf(agentOf(caps, kind), run?.model ?? null);
  // i18n-ignore: product names
  const runs = model?.label ?? (agentName(kind) || "Agent");
  return (
    <span className="inline-flex items-center gap-x-[6px]">
      {words && <span className="text-accent">{words()}</span>}
      {words && <span aria-hidden="true">·</span>}
      <span>{runs}</span>
    </span>
  );
}

/**
 * On a Case's step (in the Cases list and in its Case's Chain): the way to
 * the chat it was handed to, with that chat's state on it.
 */
export const stepChatRowMark: RowMark = {
  id: "agent-chat",
  Button: StepChatLink,
  useNote: useStepChatNote,
};
